import { SuzService } from '@skylab-shared/business';
import { ConsoleLogger, L10nBase, RootContextBase } from '@skylab-shared/core';
import { FiscalDeviceModel } from '@skylab-shared/dataModel';
import { Suz18Client, SUZ18Header, Suz18Types } from '@skylab-shared/suz';
import { v4 as uuid } from 'uuid';
import { ArticlePaymentMethodObject, ArticleVatRateObject } from '../common/suz-article';
import { DocumentToFiscalize, ProductToFiscalize } from '../common/suz-helper-types';
import { SuzInternalPrintReceiptResponse, SuzInternalResponse } from '../common/suz-internal-response';
import { SuzInternalStatus } from '../common/suz-internal-status';
import { SuzCredentials } from '../queries/suz-credentials.query-base';
import { PrintReceiptOperationManager } from './utils/suz-prepare-receipt-data';

const SUBIEKT_CLIENT_APP = 6;

export class Suz18ServiceBase implements SuzService {
	#suzClient!: Suz18Client;
	readonly #ready!: Promise<void>;
	readonly #logger = new ConsoleLogger(this.constructor.name);
	public version = '18';

	public constructor(
		private readonly l10n: L10nBase,
		private readonly rootContext: RootContextBase,
		credentials: SuzCredentials
	) {
		this.#ready = this.refreshCredentialsAsync(credentials);
	}

	public async getBaseUrlAsync(): Promise<string> {
		await this.#ready;
		return this.#suzClient.suzBaseUrl;
	}

	public async refreshCredentialsAsync(credentials: SuzCredentials): Promise<void> {
		const credentialsData = credentials && !credentials.isDefault ? credentials : { login: 'admin', password: 'admin'};
		const params: SUZ18Header = {
			ClientData: {
				ClientAppInfo: {
					KnownClientApp: SUBIEKT_CLIENT_APP,
					PermissionVersion: '1.0.0.0'
				},
				ClientIdentityName: `${credentialsData.login}_${uuid()}`,
				ClientVersion: '1.0.0.0',
				Password: credentialsData.password,
				Username: credentialsData.login
			}
		};
		this.#suzClient = await Suz18Client.createAsync(params);
	}

	public async checkConnectionAsync(): Promise<SuzInternalResponse> {
		await this.#ready;
		const result = await this.#suzClient.getExistingDevicesNamesAsync();

		if (Array.isArray(result)) {
			return {
				type: SuzInternalStatus.Success,
			};
		} else if (result) {
			return this.parseOperationResultToInternalStatus(result);
		} else {
			return {
				type: SuzInternalStatus.Error
			} as SuzInternalResponse;
		}
	}

	public async getExistingPrintersNamesAsync(): Promise<SuzInternalResponse<string[]>> {
		await this.#ready;
		try {
			const devices = await this.#suzClient.getExistingDevicesNamesAsync();
			if (!Array.isArray(devices)) return {
				type: SuzInternalStatus.Error,
			};

			const devicesInfos = await Promise.all([...devices.map(e => this.#suzClient.getDeviceInfoAsync(e))]);
			const modelsInfos = await Promise.all([...devicesInfos.map(e => this.#suzClient.getModelInfoAsync(e!.DeviceDefinition.ModelName))]);
			const printerModels = modelsInfos.filter(e => e!.ModelTicket.DeviceType === 'FiscalPrinter').map(e => e!.ModelTicket.Name);
			const res = devicesInfos.filter(e => printerModels.includes(e!.DeviceDefinition.ModelName)).map(e => e!.DeviceDefinition.DeviceName);
			return {
				type: SuzInternalStatus.Success,
				data: res
			};
		} catch(err) {
			return {
				type: SuzInternalStatus.Error,
			};
		}
	}

	public async getPrinterVatTableAsync(printerName: string): Promise<ArticleVatRateObject | null> {
		await this.#ready;
		const params: Suz18Types.OperationBeginData = {
			DeviceName: printerName,
			Operation: {
				ReadVatTable: {
					Args: {
						BusinessParamsWithValues: {}
					}
				}
			}
		};
		const result = await this.#suzClient.runPrinterOperationAsync<Suz18Types.GetVatRatesOperationResponse>(params);
		const parsedResult = this.parseOperationResultToInternalStatus(result);
		if (parsedResult.type === SuzInternalStatus.Success && parsedResult.data) {
			const vatRates = parsedResult.data['Dictionary<VatCode,Vat>'];
			Object.keys(vatRates).forEach(key => {
				if (vatRates[key].Value != null) vatRates[key].Value = Number(vatRates[key].Value)
			});
			return vatRates;
		} else {
			return null;
		}
	}

	public async printReceiptAsync(document: DocumentToFiscalize, products: ProductToFiscalize[], device: FiscalDeviceModel): Promise<SuzInternalPrintReceiptResponse> {
		await this.#ready;
		const deviceDetails = await this.#suzClient.getDeviceInfoAsync(device.name);
		const manager = await PrintReceiptOperationManager(this.rootContext, deviceDetails, device, document, products);
		const data = manager.prepareData();

		if (manager.getLastError()) {
			const text = {
				VatRateTechnicalZeroMapping: this.l10n.instant('business.suz.messages.vat-rate-technical-zero-mapping-error'),
				VatRateMapping: this.l10n.instant('business.suz.messages.vat-rate-mapping-error'),
				PaymentMethodMapping: this.l10n.instant('business.suz.messages.payment-method-mapping-error'),
			}[manager.getLastError()!];

			return {
				type: SuzInternalStatus.MappingError,
				suzMessages: [text]
			};
		}
		const params: Suz18Types.OperationBeginData = {
			DeviceName: device.name,
			Operation: {
				PrintReceipt: {
					Args: data
				}
			}
		};

		const result = await this.#suzClient.runPrinterOperationAsync<Suz18Types.PrintReceiptResponse>(params);
		const parsedResult = this.parseOperationResultToInternalStatus(result);
		if (parsedResult.type === SuzInternalStatus.Success) {
			return {
				type: SuzInternalStatus.Success,
				data: {
					number: parsedResult?.data?.FiscalDeviceAndDocumentTickets?.DocumentTicket.Number || '',
					serialNumber: parsedResult?.data?.FiscalDeviceAndDocumentTickets?.FiscalDeviceTicket.SerialNumber || ''
				}
			};
		} else {
			return {...parsedResult};
		}
	}

	public async printDailyReportAsync(printer: {name: string}): Promise<SuzInternalResponse> {
		await this.#ready;
		const params: Suz18Types.OperationBeginData = {
			DeviceName: printer.name,
			Operation: {
				PrintDailyReport: {
					Args: {
						BusinessParamsWithValues: {}
					}
				}
			}
		};
		const result = await this.#suzClient.runPrinterOperationAsync<never>(params);
		const parsedResult = this.parseOperationResultToInternalStatus(result);
		return parsedResult;
	}

	public async getPrinterPaymentMethodsAsync(printerName: string): Promise<SuzInternalResponse<ArticlePaymentMethodObject>> {
		await this.#ready;
		const params: Suz18Types.OperationBeginData = {
			DeviceName: printerName,
			Operation: {
				ReadPaymentTerms: {
					Args: {
						BusinessParamsWithValues: {}
					}
				}
			}
		};
		const result = await this.#suzClient.runPrinterOperationAsync<Suz18Types.GetPaymentMethodsOperationResponse>(params);
		const parsedResult = this.parseOperationResultToInternalStatus(result);
		if (parsedResult.type === SuzInternalStatus.Success && parsedResult.data) {
			return {
				type: SuzInternalStatus.Success,
				data: parsedResult.data['Dictionary<Int32,String>']
			};

		} else if (parsedResult.type === SuzInternalStatus.OperationNotExecuted) {
			return parsedResult;
		} else {
			return {
				type: SuzInternalStatus.Error
			};
		}
	}

	public async checkPrinterConnectionAsync(printerName: string): Promise<SuzInternalResponse<null>> {
		await this.#ready;
		const params: Suz18Types.OperationBeginData = {
			DeviceName: printerName,
			Operation: {
				CheckConnection: {
					Args: {
						BusinessParamsWithValues: {}
					}
				}
			}
		};
		const result = await this.#suzClient.runPrinterOperationAsync(params);
		const parsedResult = this.parseOperationResultToInternalStatus(result);
		return parsedResult;
	}

	private parseOperationResultToInternalStatus<T>(result: Suz18Types.OperationEndResponse<T> | Suz18Types.PrinterOperationErrorResponse | null | undefined): SuzInternalResponse<T> {
		function isSuzHttpError(data: Suz18Types.OperationEndResponse<T> | Suz18Types.PrinterOperationErrorResponse): data is Suz18Types.PrinterOperationErrorResponse {
			return !!(data as Suz18Types.PrinterOperationErrorResponse)?.Extensions;
		}

		if (!result) {
			return {
				type: SuzInternalStatus.Error,
				suzMessages: [this.l10n._T('business.suz.messages.suz-connection-error')]
			};
		}

		return (() => {
			try {
				if (isSuzHttpError(result)) {
					if (result.Extensions.exceptionDetail?.ServiceExceptionDetail?.Type === 'DeviceNotFoundException') {
						return {
							type: SuzInternalStatus.DeviceNotExistsError,
							suzMessages: [this.l10n._T('business.suz.messages.device-not-exists-error')]
						};
					} else if (result.Extensions.exceptionDetail?.ServiceExceptionDetail?.Type === 'IncorrectUserNameOrPasswordException') {
						return {
							type: SuzInternalStatus.InvalidCredentials,
							suzMessages: [this.l10n._T('business.suz.messages.invalid-credentials')]
						};
					} else if (result.Extensions.exceptionDetail?.ServiceExceptionDetail?.Type === 'DeviceTurnedOffException') {
						return {
							type: SuzInternalStatus.Error,
							suzMessages: [result.Extensions.exceptionDetail?.ServiceExceptionDetail?.Message]
						};
					} else if (result.Extensions.exceptionDetail?.AccessToOperationDeniedExceptionDetail) {
						const data = result.Extensions.exceptionDetail?.AccessToOperationDeniedExceptionDetail.WykonywanaOperacjaInfoDane;
						let message = this.l10n.instant('business.suz.messages.device-is-busy');
						if (data) message += ` ClientName: ${data.ClientName}, StartTime: ${data.StartTime}, OperationName: ${data.OperationName}, DeviceName: ${data.DeviceName}.`;
						return {
							type: SuzInternalStatus.Error,
							suzMessages: [message]
						};
					}
					return { type: SuzInternalStatus.Error, suzMessages: [this.l10n._T('business.suz.messages.suz-connection-error')] };
				} else if (result.Errors?.length){
					if (result.Errors?.find(e => e.OperationNotExecuted)) {
						const validation = result.Errors.find(e => e.OperationNotExecuted);
						const messages = this.addDotAtTheEnd(`${validation!.OperationNotExecuted!.GeneralComment}: ${validation!.OperationNotExecuted!.DetailedComment}`);
						return {
							type: SuzInternalStatus.OperationNotExecuted,
							suzMessages: [messages]
						};
					} else if (result.Errors?.find(e => e.DailyReportRequired)) {
						const validation = result.Errors.find(e => e.DailyReportRequired);
						const messages = this.addDotAtTheEnd(`${validation!.DailyReportRequired!.GeneralComment}: ${validation!.DailyReportRequired!.DetailedComment}`);
						return {
							type: SuzInternalStatus.DailyReportRequired,
							suzMessages: [messages]
						};
					} else if (result.Errors?.find(e => e.NoConnection)) {
						const validation = result.Errors.find(e => e.NoConnection);
						const messages = this.addDotAtTheEnd(`${validation!.NoConnection!.GeneralComment}: ${validation!.NoConnection!.DetailedComment}`);
						return {
							type: SuzInternalStatus.DeviceConnectionError,
							suzMessages: [messages]
						};
					} else if (result.Errors.find(e => e.DeviceError)) {
						const validations = result.Errors.filter(e => e.DeviceError);
						const messages = validations.map(e => this.addDotAtTheEnd(`${e.DeviceError!.GeneralComment}: ${e.DeviceError!.DetailedComment}`));
						return {
							type: SuzInternalStatus.DeviceError,
							suzMessages: messages
						};
					} else {
						const messages: string[] = result.Errors?.flatMap(error =>
							Object.values(error)
								.filter(validation =>
									!!validation && typeof validation === 'object' && 'GeneralComment' in validation && 'DetailedComment' in validation
								)
								.map((validation: any) => this.addDotAtTheEnd(`${validation.GeneralComment}: ${validation.DetailedComment}`))
						) || [];
						const base = {
							type: SuzInternalStatus.Error,
							suzMessages: [...messages || this.l10n._T('business.suz.messages.suz-connection-error')]
						};
						return base;
					}
				} else {
					return {
						type: SuzInternalStatus.Success,
						data: result.Data,
					};
				}
			} catch(err) {
				this.#logger.error(err);
				return {
					type: SuzInternalStatus.Error,
					suzMessages: [this.l10n._T('business.suz.messages.suz-connection-error')]
				};
			}
		})() as SuzInternalResponse<T>;
	}

    private addDotAtTheEnd(text: string): string {
        return text.concat(text.endsWith('.') ? ' ' : '. ');
    }
}
